'use client';

import { Tooltip } from '@mantine/core';
import type { PropsWithChildren, ReactNode } from 'react';

import { EligibilityResult } from '@dte/otw-azure-functions/src/common/interfaces/api/EligibilityResult';
import type { PremiseDataDocument } from '@dte/otw-azure-functions/src/common/interfaces/containers/premises/PremiseData';
import { getPremiseEligibilityResult } from '@dte/otw-azure-functions/src/common/premises/data/attributes/eligibility/getPremiseEligibilityResult';

import { EligibilityMessages } from '../(premiseId)/eligibility/PremiseEligibility';
import { PremiseLink } from '../(premiseId)/premiseLink/PremiseLink';
import { addClassName } from '../../../../../components/form/addClassName';
import styles from './PremiseMarker.module.scss';

const markerClassName: Record<EligibilityResult, string | undefined> = {
	[EligibilityResult.ElectricActive]: styles['active'],
	[EligibilityResult.DNP]: styles['warning'],
	[EligibilityResult.ElectricInactive]: styles['inactive'],
	[EligibilityResult.GasOnly]: styles['inactive'],
	[EligibilityResult.NoService]: styles['inactive'],
	[EligibilityResult.NotFound]: styles['inactive'],
};

export interface PremiseMarkerProps extends PropsWithChildren {
	premiseData: PremiseDataDocument | undefined;
	size?: 'small' | 'medium';
}

function PremiseDetailPopup({ premiseData, children }: PremiseMarkerProps): ReactNode {
	const eligibility = getPremiseEligibilityResult(premiseData);

	// IF no message, don't add a popup
	if (!eligibility) {
		return children;
	}
	const message = EligibilityMessages[eligibility];
	if (!message) {
		return children;
	}

	const tooltipClass = addClassName(styles['premiseDetailTooltip'], markerClassName[eligibility]);
	const tooltipArrowClass = addClassName(styles['premiseDetailTooltipArrow'], markerClassName[eligibility]);

	return (
		<Tooltip
			withArrow={true}
			arrowSize={8}
			arrowOffset={16}
			position={'left'}
			zIndex={9999}
			label={message}
			classNames={{ tooltip: tooltipClass, arrow: tooltipArrowClass }}
		>
			<div>{children}</div>
		</Tooltip>
	);
}

export function PremiseMarker(props: PremiseMarkerProps): ReactNode {
	const premiseData = props.premiseData;
	if (!premiseData) {
		return null;
	}

	const premiseId = premiseData.premiseId;

	const eligibility = getPremiseEligibilityResult(premiseData);
	let markerClass = styles['marker'];
	if (eligibility) {
		markerClass = addClassName(markerClass, markerClassName[eligibility]);
	}
	if (props.size === 'small') {
		markerClass = addClassName(markerClass, styles['small']);
	}

	return (
		<PremiseDetailPopup {...props}>
			<PremiseLink premiseId={premiseId}>
				<div className={markerClass}>{props.children}</div>
			</PremiseLink>
		</PremiseDetailPopup>
	);
}
